'use client'

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Link as LinkIcon,
  QrCode,
  Globe,
  Film,
  ShoppingBag,
  ClipboardList,
  Gift,
  Hash,
  Camera,
  Zap,
  Search,
  ArrowRight,
  Sparkles,
  Lock,
  Download,
  Copy,
  Check,
  CheckCircle2,
  TrendingUp,
  Sliders,
  ShieldAlert,
  Smartphone,
  Layers,
  BarChart2
} from 'lucide-react'
import Link from 'next/link'
import { useUser } from '@clerk/nextjs'

const navItems = [
  { id: 'smart-links', label: 'Smart Links', icon: LinkIcon },
  { id: 'qr-codes', label: 'Dynamic QR', icon: QrCode },
  { id: 'link-in-bio', label: 'Bio & Reels', icon: Film },
  { id: 'digital-stores', label: 'Digital Stores', icon: ShoppingBag },
  { id: 'forms-polls', label: 'Forms & Polls', icon: ClipboardList },
  { id: 'referral-system', label: 'Referral Engine', icon: Gift },
]

export function HowItWorksFeatures() {
  const { user } = useUser()
  const session = !!user
  const [copiedLink, setCopiedLink] = useState(false)
  const [copiedRef, setCopiedRef] = useState(false)
  const [selectedPollOption, setSelectedPollOption] = useState<number>(0)

  const handleCopyLink = () => {
    setCopiedLink(true)
    setTimeout(() => setCopiedLink(false), 2000)
  }

  const handleCopyRef = () => {
    setCopiedRef(true)
    setTimeout(() => setCopiedRef(false), 2000)
  }

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  return (
    <div className="space-y-16 sm:space-y-24">
      {/* ── Feature Navigation Pills ── */}
      <div className="sticky top-20 z-30 py-3 backdrop-blur-xl bg-black/60 border-y border-white/5 flex items-center justify-center overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 px-4 max-w-full">
          {navItems.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide whitespace-nowrap bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white border border-white/10 transition-all cursor-pointer hover:border-primary/40 active:scale-95"
              >
                <Icon className="w-3.5 h-3.5 text-primary" />
                <span>{item.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-16 sm:space-y-24 max-w-[1150px] mx-auto px-6">
        {/* ═══════════════════════════════════════════════════════════════
            FEATURE 1: SMART LINKS & CUSTOM DOMAINS (Text Left, Mockup Right)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          id="smart-links"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 sm:p-12 lg:p-16 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 relative overflow-hidden shadow-2xl"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary uppercase tracking-wider">
                <LinkIcon className="w-3.5 h-3.5 text-primary" />
                <span>Smart Links &amp; CNAME Routing</span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white uppercase leading-tight">
                Shorten, brand &amp; <span className="text-primary">track every click.</span>
              </h3>

              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed">
                Turn long, ugly URLs into high-converting branded assets. Route visitors dynamically by device or country, protect private pages with passwords, and measure conversions at the edge.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Globe className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Custom Domains</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Map your own CNAME domain (e.g. <span className="font-mono text-primary">go.brand.com</span>) with auto-SSL.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Smartphone className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Device Targeting</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Send iOS users to App Store, Android to Google Play, and web to your landing page.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Zap className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Edge Latency</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Under 100ms global redirect response with privacy-first cookieless analytics.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-white rounded-full font-bold text-sm hover:bg-primary/90 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] hover:scale-105 active:scale-95"
                >
                  <span>Build Your First Smart Link</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right Mockup: Smart Link Card */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="w-full max-w-[340px] rounded-3xl bg-[#0A0A0A] border border-white/10 p-5 shadow-2xl space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-white/5">
                  <span className="text-[10px] font-mono text-white/40">Short Link Simulator</span>
                  <span className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-bold">● ACTIVE</span>
                </div>

                <div className="p-3 rounded-2xl bg-white/[0.03] border border-white/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-primary">lenklyst.com/summer24</span>
                    <button
                      onClick={handleCopyLink}
                      className="p-1 rounded bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all text-[10px] flex items-center gap-1 cursor-pointer"
                    >
                      {copiedLink ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedLink ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <p className="text-[11px] text-white/40 truncate font-mono">
                    https://store.yourbrand.com/deals/summer?ref=social
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.02] border border-white/5 text-xs">
                    <span className="text-white/60">Dynamic Device Rule</span>
                    <span className="font-mono text-[10px] text-primary font-bold">iOS → App Store</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.02] border border-white/5 text-xs">
                    <span className="text-white/60">Password Protection</span>
                    <span className="font-mono text-[10px] text-emerald-400 font-bold">Enabled</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-1 border-t border-white/5 text-center">
                  <div className="p-2 rounded-xl bg-white/[0.02]">
                    <p className="text-[10px] text-white/40">Clicks</p>
                    <p className="text-sm font-mono font-bold text-white">1,240</p>
                  </div>
                  <div className="p-2 rounded-xl bg-white/[0.02]">
                    <p className="text-[10px] text-white/40">Countries</p>
                    <p className="text-sm font-mono font-bold text-white">48</p>
                  </div>
                  <div className="p-2 rounded-xl bg-white/[0.02]">
                    <p className="text-[10px] text-white/40">Mobile</p>
                    <p className="text-sm font-mono font-bold text-primary">94%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            FEATURE 2: DYNAMIC QR CODE ENGINE (Mockup Left, Text Right)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          id="qr-codes"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 sm:p-12 lg:p-16 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 relative overflow-hidden shadow-2xl"
        >
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Mockup: QR Card on desktop */}
            <div className="lg:col-span-5 flex justify-center lg:order-1 order-2">
              <div className="w-full max-w-[340px] rounded-3xl bg-[#0A0A0A] border border-white/10 p-5 shadow-2xl space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-white/5">
                  <span className="text-[10px] font-mono text-white/40">Dynamic Vector QR</span>
                  <span className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-bold">● EDITABLE</span>
                </div>

                {/* Simulated QR Code Graphic */}
                <div className="aspect-square w-full max-w-[180px] mx-auto rounded-2xl bg-white p-3 flex items-center justify-center relative overflow-hidden shadow-lg">
                  <div className="w-full h-full border-4 border-black p-2 flex flex-col justify-between">
                    <div className="flex justify-between">
                      <div className="w-8 h-8 bg-black rounded" />
                      <div className="w-8 h-8 bg-black rounded" />
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold">
                        L
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <div className="w-8 h-8 bg-black rounded" />
                      <div className="w-3 h-3 bg-black rounded self-end" />
                    </div>
                  </div>
                  {/* Scan Line animation */}
                  <motion.div
                    animate={{ y: [-90, 90, -90] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute inset-x-0 h-0.5 bg-primary shadow-[0_0_8px_#3b82f6]"
                  />
                </div>

                <div className="p-3 rounded-2xl bg-white/[0.02] border border-white/5 space-y-1 text-center">
                  <p className="text-[11px] font-mono font-bold text-white truncate">lenklyst.com/qr/event-menu</p>
                  <p className="text-[9px] text-white/40 truncate">Destination URL updated 2 min ago</p>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-white/5 text-xs text-white/60">
                  <span>890 Scans</span>
                  <span className="font-mono text-primary text-[10px] font-bold">Vector SVG Ready</span>
                </div>
              </div>
            </div>

            {/* Right Text */}
            <div className="lg:col-span-7 space-y-6 lg:order-2 order-1">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary uppercase tracking-wider">
                <QrCode className="w-3.5 h-3.5 text-primary" />
                <span>Dynamic QR Engine</span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white uppercase leading-tight">
                Print once. <span className="text-primary">Update destination anytime.</span>
              </h3>

              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed">
                Never reprint packaging, table tents, or event flyers again. Update redirect endpoints in real time post-print, analyze physical scans by city and device, and export print-ready vector SVGs.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Sliders className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Post-Print Editing</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Change destination links on demand even after printing 10,000 brochures.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Download className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Vector SVG &amp; PNG</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Crisp, infinite-scale files designed for high-resolution print production.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <BarChart2 className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Scan Analytics</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Monitor scan volume, time of day, location, and mobile OS breakdown.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-white rounded-full font-bold text-sm hover:bg-primary/90 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] hover:scale-105 active:scale-95"
                >
                  <span>Generate Dynamic QR Codes</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            FEATURE 3: LINK IN BIO & REELS TO LINK (Text Left, Mockup Right)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          id="link-in-bio"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 sm:p-12 lg:p-16 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 relative overflow-hidden shadow-2xl"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary uppercase tracking-wider">
                <Film className="w-3.5 h-3.5 text-primary" />
                <span>Link in Bio &amp; Reels to Link</span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white uppercase leading-tight">
                Link in bio &amp; <span className="text-primary">reels to link.</span>
              </h3>

              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed">
                Never lose followers in the comments. Tag any reel with a short #code or thumbnail so your audience taps your bio link and finds the exact link in one tap.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Hash className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">#Code Spotlight</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Visitors type the code into an instant search pill to trigger an immediate link popup.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Camera className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Visual Video Grid</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Followers instantly spot the exact reel cover thumbnail they just watched.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Zap className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Direct Code URL</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Link sticker <span className="font-mono text-primary">?code=24</span> auto-opens and highlights the resource on load.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-white rounded-full font-bold text-sm hover:bg-primary/90 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] hover:scale-105 active:scale-95"
                >
                  <span>Try Reel Codes on Your Bio Page</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right Column: Live Mockup Card */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="w-full max-w-[340px] rounded-3xl bg-[#0A0A0A] border border-white/10 p-5 shadow-2xl space-y-3.5">
                <div className="flex items-center justify-between pb-2 border-b border-white/5">
                  <span className="text-[10px] font-mono text-white/40">lenklyst.com/bio/creator</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                </div>

                <div className="p-2.5 rounded-full bg-white/5 border border-primary/40 flex items-center gap-2 text-xs">
                  <Search className="w-3.5 h-3.5 text-primary" />
                  <span className="font-mono text-white text-[11px] font-bold">#24</span>
                  <span className="text-[9px] text-emerald-400 font-mono ml-auto bg-emerald-500/10 px-1.5 py-0.5 rounded">MATCH FOUND</span>
                </div>

                <div className="p-3 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-between gap-3 animate-pulse">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-9 h-11 rounded-lg bg-white/10 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
                      <Film className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">Free Notion Planner</p>
                      <p className="text-[9px] text-primary/70 font-mono truncate">Code: #24</p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 rounded-lg bg-primary text-white text-[10px] font-bold shadow-sm">
                    Get Link
                  </span>
                </div>

                <div className="pt-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-2">As Seen on Reels</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="aspect-[3/4] rounded-xl bg-white/[0.03] border border-white/10 p-2 flex flex-col justify-between relative overflow-hidden group">
                      <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-black/70 text-primary w-fit">#24</span>
                      <p className="text-[9px] font-semibold text-white/90 line-clamp-1">Notion Plan</p>
                    </div>
                    <div className="aspect-[3/4] rounded-xl bg-white/[0.03] border border-white/10 p-2 flex flex-col justify-between relative overflow-hidden group">
                      <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-black/70 text-primary w-fit">#LUT</span>
                      <p className="text-[9px] font-semibold text-white/90 line-clamp-1">Color Preset</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            FEATURE 4: DIGITAL PRODUCT STOREFRONTS (Mockup Left, Text Right)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          id="digital-stores"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 sm:p-12 lg:p-16 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 relative overflow-hidden shadow-2xl"
        >
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Mockup */}
            <div className="lg:col-span-5 flex justify-center lg:order-1 order-2">
              <div className="w-full max-w-[340px] rounded-3xl bg-[#0A0A0A] border border-white/10 p-5 shadow-2xl space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-white/5">
                  <span className="text-[10px] font-mono text-white/40">Digital Storefront</span>
                  <span className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-bold">● 95% PAYOUT</span>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-[9px] font-mono uppercase tracking-wider text-primary font-bold">Digital Download</span>
                      <h4 className="text-sm font-bold text-white">Freelance Notion OS</h4>
                    </div>
                    <span className="text-sm font-mono font-bold text-white bg-primary/20 px-2.5 py-1 rounded-lg text-primary">
                      $49
                    </span>
                  </div>
                  <p className="text-[11px] text-white/50 leading-relaxed">
                    Complete client CRM, project pipelines, and invoicing workspace.
                  </p>
                  <div className="flex items-center gap-2 pt-1 text-[10px] text-white/40">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                    <span>Instant .ZIP File Delivery</span>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between text-xs">
                  <span className="text-white/40">Fee Breakdown</span>
                  <span className="text-white/80 font-mono text-[11px]">
                    You keep: <strong className="text-emerald-400">$46.55</strong> (95%)
                  </span>
                </div>

                <button className="w-full py-3 bg-primary text-white rounded-xl text-xs font-bold hover:bg-primary/90 transition-all flex items-center justify-center gap-2 shadow-sm">
                  <ShoppingBag className="w-3.5 h-3.5" />
                  <span>Buy Now • Instant Access</span>
                </button>
              </div>
            </div>

            {/* Right Text */}
            <div className="lg:col-span-7 space-y-6 lg:order-2 order-1">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary uppercase tracking-wider">
                <ShoppingBag className="w-3.5 h-3.5 text-primary" />
                <span>Creator Storefronts</span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white uppercase leading-tight">
                Sell digital goods. <span className="text-primary">Keep 95% of revenue.</span>
              </h3>

              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed">
                Monetize your audience with instant digital downloads, presets, ebooks, and templates. Retain 95% of your gross earnings with our ultra-low 5% platform fee.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Download className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Instant Fulfillment</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Automated, secure download links delivered directly to customer inboxes.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <TrendingUp className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Keep 95% Payout</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Only 5% platform fee—far lower than alternative marketplaces charging 10%+.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Layers className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Bio Integration</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Embed product cards right inside your bio page or share standalone checkouts.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-white rounded-full font-bold text-sm hover:bg-primary/90 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] hover:scale-105 active:scale-95"
                >
                  <span>Launch Your Storefront</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            FEATURE 5: INTERACTIVE FORMS & VIRAL POLLS (Text Left, Mockup Right)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          id="forms-polls"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 sm:p-12 lg:p-16 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 relative overflow-hidden shadow-2xl"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary uppercase tracking-wider">
                <ClipboardList className="w-3.5 h-3.5 text-primary" />
                <span>Forms &amp; Viral Polls</span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white uppercase leading-tight">
                Collect insights &amp; <span className="text-primary">capture leads.</span>
              </h3>

              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed">
                Deploy beautiful surveys, customer feedback forms, and viral opinion polls in seconds. Capture zero-party leads and inspect live response analytics on your dashboard.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Sliders className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Visual Block Builder</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Add multiple-choice, star rating, email capture, and custom fields with ease.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <BarChart2 className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Live Answer Logs</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Watch incoming responses in real time with visual response charts and CSV exports.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Globe className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Universal Embeds</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Share as standalone links, embed on bio profiles, or pop up on redirect gates.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-white rounded-full font-bold text-sm hover:bg-primary/90 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] hover:scale-105 active:scale-95"
                >
                  <span>Build an Interactive Form</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right Mockup: Interactive Poll Card */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="w-full max-w-[340px] rounded-3xl bg-[#0A0A0A] border border-white/10 p-5 shadow-2xl space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-white/5">
                  <span className="text-[10px] font-mono text-white/40">Interactive Poll Widget</span>
                  <span className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-bold">● LIVE (1,240 VOTES)</span>
                </div>

                <div className="space-y-1">
                  <h4 className="text-xs font-bold text-white">What feature drives the most sales?</h4>
                  <p className="text-[10px] text-white/40">Select an option to view live community results.</p>
                </div>

                <div className="space-y-2">
                  {[
                    { label: 'Reel Codes & Bio Links', pct: 62 },
                    { label: 'Word-of-Mouth Referrals', pct: 24 },
                    { label: 'Dynamic QR Code Scans', pct: 14 }
                  ].map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedPollOption(idx)}
                      className={`w-full p-2.5 rounded-xl border text-left transition-all relative overflow-hidden cursor-pointer ${
                        selectedPollOption === idx
                          ? 'border-primary bg-primary/10'
                          : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]'
                      }`}
                    >
                      <div
                        className="absolute inset-y-0 left-0 bg-primary/15 rounded-xl transition-all duration-500"
                        style={{ width: `${opt.pct}%` }}
                      />
                      <div className="relative z-10 flex items-center justify-between text-xs">
                        <span className="font-semibold text-white truncate pr-2">{opt.label}</span>
                        <span className="font-mono text-[10px] text-primary font-bold">{opt.pct}%</span>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="pt-1 border-t border-white/5 flex items-center justify-between text-[10px] text-white/40">
                  <span>Zero-Party Data Capture</span>
                  <span className="text-emerald-400 font-mono">Response Logged ✓</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════
            FEATURE 6: MILESTONE REFERRAL SYSTEMS (Mockup Left, Text Right)
        ═══════════════════════════════════════════════════════════════ */}
        <motion.div
          id="referral-system"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 sm:p-12 lg:p-16 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 relative overflow-hidden shadow-2xl"
        >
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Mockup */}
            <div className="lg:col-span-5 flex justify-center lg:order-1 order-2">
              <div className="w-full max-w-[340px] rounded-3xl bg-[#0A0A0A] border border-white/10 p-5 shadow-2xl space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-white/5">
                  <span className="text-[10px] font-mono text-white/40">Ambassador Referral Hub</span>
                  <span className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-bold">● TIER 2 ACTIVE</span>
                </div>

                <div className="p-3 rounded-2xl bg-white/[0.03] border border-white/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-primary">lenklyst.com/ref/sarah</span>
                    <button
                      onClick={handleCopyRef}
                      className="p-1 rounded bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all text-[10px] flex items-center gap-1 cursor-pointer"
                    >
                      {copiedRef ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedRef ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40">Share with friends to unlock rewards automatically</p>
                </div>

                {/* Progress Bar */}
                <div className="space-y-1.5 p-3 rounded-2xl bg-white/[0.02] border border-white/5">
                  <div className="flex justify-between text-xs">
                    <span className="text-white/60">Milestone Progress</span>
                    <span className="font-mono font-bold text-white">8 / 10 Referrals</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500 w-[80%]" />
                  </div>
                  <p className="text-[10px] text-primary font-mono pt-0.5">2 more invites to unlock: Pro Plan (30 Days)</p>
                </div>

                <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20 flex items-center gap-2 text-xs">
                  <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                  <span className="text-white text-[11px]">Reward Unlocked: <strong>20% Lifetime Discount</strong></span>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-white/5 text-[10px] text-white/40">
                  <span>Anti-Fraud Guard</span>
                  <span className="text-emerald-400 font-mono">0 Bot Registrations</span>
                </div>
              </div>
            </div>

            {/* Right Text */}
            <div className="lg:col-span-7 space-y-6 lg:order-2 order-1">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary uppercase tracking-wider">
                <Gift className="w-3.5 h-3.5 text-primary" />
                <span>Milestone Referral Engine</span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white uppercase leading-tight">
                Turn your audience into <span className="text-primary">active brand advocates.</span>
              </h3>

              <p className="text-base sm:text-lg text-white/60 font-light leading-relaxed">
                Supercharge organic word-of-mouth growth. Issue unique invite links, track milestone scores, unlock coupons or premium perks, and filter out fake bot clicks with intelligent fraud prevention.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <LinkIcon className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Personal Invite Links</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Auto-generate member referral links with custom slugs for sharing across social channels.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Tiered Milestones</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Trigger automated coupon codes, free trial extensions, or digital downloads at set targets.
                  </p>
                </div>

                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                    <ShieldAlert className="w-4 h-4" />
                  </div>
                  <h4 className="text-sm font-bold text-white">Anti-Fraud Engine</h4>
                  <p className="text-xs text-white/40 leading-relaxed">
                    Advanced IP fingerprinting and velocity challenge filters to block bot traffic.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-white rounded-full font-bold text-sm hover:bg-primary/90 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] hover:scale-105 active:scale-95"
                >
                  <span>Launch a Referral Program</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
